import { FC, memo, useMemo, useCallback } from 'react'

import { GpsSupplyRequest, SupplyRequestReviewStatus } from './types/types'
import { useReviewGpsPetitionMutation } from './useGpsPetitionsQuery'
import { useToasts } from 'react-toast-notifications'

import { ReactComponent as IconEdit } from 'images/icons/icon-edit.svg'
import { ReactComponent as IconApprove } from 'images/icons/icon-approve.svg'
import { ReactComponent as IconReject } from 'images/icons/icon-reject.svg'

import styles from './ActionsMenu.module.scss'

const ICON_SIZE = 20

type ActionsMenuProps = {
  petition: GpsSupplyRequest
}

const actions = {
  APPROVE: 'APPROVE',
  REJECT: 'REJECT',
  EDIT: 'EDIT',
} as const

type Actions = typeof actions[keyof typeof actions]

export const MenuActionsPerStatus = [
  { status: SupplyRequestReviewStatus.APPROVED, actions: [] },
  { status: SupplyRequestReviewStatus.REJECTED, actions: [] },
  { status: SupplyRequestReviewStatus.PENDING, actions: [actions.APPROVE, actions.REJECT] },
]

const ActionsMenu: FC<ActionsMenuProps> = (props) => {
  const { petition } = props
  const { addToast } = useToasts()
  const reviewMutation = useReviewGpsPetitionMutation()

  const handleClick = useCallback(
    (value: Actions) => {
      if (reviewMutation.isLoading) return

      switch (value) {
        case actions.EDIT:
          addToast('Edit functionality not yet implemented', {
            appearance: 'info',
            autoDismiss: true,
          })
          break

        case actions.APPROVE:
          reviewMutation.mutate(
            {
              reqId: petition.id,
              decision: SupplyRequestReviewStatus.APPROVED,
            },
            {
              onSuccess: () => {
                addToast('Location petition approved successfully', {
                  appearance: 'success',
                  autoDismiss: true,
                })
              },
              onError: (error) => {
                console.error('Failed to approve petition:', error)
                addToast('Failed to approve location petition', {
                  appearance: 'error',
                  autoDismiss: true,
                })
              },
            },
          )
          break

        case actions.REJECT:
          reviewMutation.mutate(
            {
              reqId: petition.id,
              decision: SupplyRequestReviewStatus.REJECTED,
            },
            {
              onSuccess: () => {
                addToast('Location petition rejected successfully', {
                  appearance: 'success',
                  autoDismiss: true,
                })
              },
              onError: (error) => {
                console.error('Failed to reject petition:', error)
                addToast('Failed to reject location petition', {
                  appearance: 'error',
                  autoDismiss: true,
                })
              },
            },
          )
          break

        default:
          break
      }
    },
    [petition.id, reviewMutation, addToast],
  )

  const getActionDataFromStatus = (status: string) => {
    let actionMenuData

    switch (status) {
      case actions.APPROVE:
        actionMenuData = {
          action: actions.APPROVE,
          icon: <IconApprove width={ICON_SIZE} height={ICON_SIZE} />,
          text: 'Approve',
        }
        break
      case actions.REJECT:
        actionMenuData = {
          action: actions.REJECT,
          icon: <IconReject width={ICON_SIZE} height={ICON_SIZE} />,
          text: 'Reject',
        }
        break
      default:
        actionMenuData = {
          action: actions.EDIT,
          icon: <IconEdit width={ICON_SIZE} height={ICON_SIZE} />,
          text: 'Edit',
        }
        break
    }

    return actionMenuData
  }

  const menuOptions = useMemo(() => {
    const availableActions = MenuActionsPerStatus.find((action) => action.status === petition.reviewStatus)?.actions
    return availableActions?.map((action) => getActionDataFromStatus(action))
  }, [petition.reviewStatus])

  return (
    <div className={styles.container}>
      <div className={styles.menu}>
        {menuOptions?.map((option) => (
          <button
            type="button"
            className={`${styles.button} ${reviewMutation.isLoading ? styles.loading : ''}`}
            key={option.action}
            onClick={() => handleClick(option.action)}
            disabled={reviewMutation.isLoading}
          >
            <div className={styles.buttonContent}>
              {reviewMutation.isLoading ? (
                <>
                  <div className={styles.spinner} />
                  <label>Processing...</label>
                </>
              ) : (
                <>
                  {option.icon}
                  <label>{option.text}</label>
                </>
              )}
            </div>
          </button>
        ))}
      </div>
    </div>
  )
}

export default memo(ActionsMenu)
